import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { Infeasibility } from "./useGenerateRosterMutation";
import {
  consumePendingRosterCreateConfigSyncCache,
  invalidateRosterCreateConfigSyncCaches,
} from "../service/rosterCreateConfigSyncCache";
import { invalidateRosterCachesAndSelectLatestSchedule } from "../service/invalidateRosterCreateCaches";
import type { CurDate, CurScheduleInfo } from "@/store/scheduleStore";
import type { RosterJobStatus } from "@/hooks/useCreateRosterJob";

type UseRosterCreateJobEffectsParams = {
  readonly rosterStatus: RosterJobStatus | null | undefined;
  readonly rosterInfeasibility: Infeasibility | null | undefined;
  readonly curDate: CurDate | null;
  readonly selectedGroupId: string | null;
  readonly isAdmin: boolean;
  readonly queryClient: QueryClient;
  readonly setCurScheduleInfo: (info: CurScheduleInfo | null) => void;
  readonly setBlockingInfeasibility: (value: Infeasibility | null) => void;
};

export function useRosterCreateJobEffects({
  rosterStatus,
  rosterInfeasibility,
  curDate,
  selectedGroupId,
  isAdmin,
  queryClient,
  setCurScheduleInfo,
  setBlockingInfeasibility,
}: UseRosterCreateJobEffectsParams) {
  useEffect(() => {
    if (rosterStatus !== "SUCCESS") return;
    const year = curDate?.year;
    const month = curDate?.month;
    if (year == null || month == null) return;

    let cancelled = false;

    void (async () => {
      await invalidateRosterCachesAndSelectLatestSchedule(
        queryClient,
        {
          year,
          month,
          selectedGroupId,
          isAdmin,
        },
        setCurScheduleInfo
      );
      const syncCacheTarget = consumePendingRosterCreateConfigSyncCache();
      if (syncCacheTarget) {
        await invalidateRosterCreateConfigSyncCaches(
          queryClient,
          syncCacheTarget
        );
      }
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [
    rosterStatus,
    curDate?.year,
    curDate?.month,
    selectedGroupId,
    queryClient,
    isAdmin,
    setCurScheduleInfo,
  ]);

  useEffect(() => {
    if (rosterStatus !== "FAILED") return;

    const syncCacheTarget = consumePendingRosterCreateConfigSyncCache();
    if (!syncCacheTarget) return;

    void invalidateRosterCreateConfigSyncCaches(queryClient, syncCacheTarget);
  }, [queryClient, rosterStatus]);

  useEffect(() => {
    if (rosterInfeasibility?.severity === "blocking") {
      setBlockingInfeasibility(rosterInfeasibility);
    }
  }, [rosterInfeasibility, setBlockingInfeasibility]);

  useEffect(() => {
    if (rosterStatus === "QUEUED" || rosterStatus === "RUNNING") {
      setBlockingInfeasibility(null);
    }
  }, [rosterStatus, setBlockingInfeasibility]);
}
